<template>
  <div class="p-4 bg-white rounded-lg">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">
        Send Job Sheets & Certificates
        <span v-if="schedule.job_id" class="text-base font-normal text-gray-500 ml-3">
          (Job ID: {{ schedule.job_id }})
        </span>
      </h2>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Address</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="recipientEmail" type="email"
             placeholder="Enter recipient email address"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Subject</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="emailSubject" type="text"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Text</label>
      <StyledTextEditor v-model="emailMessage" :options="editorOptions"/>
    </div>

    <!-- CC (Send Copy To) -->
    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Send Copy To (sent as separate email)</label>
      <Chips v-model="ccEmail" class="w-full" separator="," />
      <small class="text-gray-500">Add one or more emails, separated by commas.</small>
    </div>

    <!-- Attach mode (PrimeVue RadioButton) -->
    <div class="mb-6 border rounded p-3">
      <label class="uppercase font-semibold text-sm mb-2">Attach as:</label>
      <div class="flex items-center gap-8">
        <div class="flex items-center gap-2">
          <RadioButton v-model="attachMode" inputId="attach-separate" value="separate" />
          <label for="attach-separate" class="font-medium cursor-pointer">Separate files</label>
        </div>
        <div class="flex items-center gap-2">
          <RadioButton v-model="attachMode" inputId="attach-merged" value="merged" />
          <label for="attach-merged" class="font-medium cursor-pointer">Merged PDFs</label>
        </div>
      </div>
    </div>

    <!-- Show table ONLY for separate -->
    <div class="mb-6" v-if="attachMode === 'separate'">
      <label class="block uppercase font-semibold text-sm mb-2">Attachments to Include</label>
      <table class="w-full border rounded">
        <thead>
        <tr>
          <th class="text-left py-2 px-3 border-b">Serial No</th>
          <th class="text-center py-2 px-3 border-b">Job Sheet</th>
          <th class="text-center py-2 px-3 border-b">Certificate</th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="(eq, eqIdx) in attachmentState"
            :key="eq.equipment_id || eq.serial_number || `eq-${eqIdx}`"
        >
          <!-- Serial No -->
          <td class="py-2 px-3 border-b font-semibold">
            {{ eq.serial_number }}
          </td>
          <!-- Job Sheet checkbox -->
          <td class="py-2 px-3 border-b text-center">
            <Checkbox
                v-if="eq.jobSheet && (eq.jobSheet.status === 'Completed' || eq.jobSheet.status === 'CompletedWithFurtherWork')"
                v-model="selectedFiles"
                :value="`job_${eq.jobSheet.job_sheet_id}`"
                :inputId="`job_${eq.jobSheet.job_sheet_id}`"
            />
            <span v-else-if="eq.jobSheet">—</span>
          </td>
          <!-- Certificates checkboxes -->
          <td class="py-2 px-3 border-b text-center">
            <div
                v-for="(cert, cIdx) in eq.certificates"
                :key="cert.certificate_id || `${eq.equipment_id || eq.serial_number || eqIdx}-cert-${cIdx}`"
                class="flex items-center justify-center mb-1"
            >
              <Checkbox
                  v-if="cert.status === 'completed'"
                  v-model="selectedFiles"
                  :value="`cert_${cert.certificate_id}`"
                  :inputId="`cert_${cert.certificate_id}`"
              />
              <span v-if="cert.status === 'completed'" class="ml-1 text-xs font-semibold text-gray-700 uppercase">
                {{ cert.shortName }}
              </span>
              <span v-else>—</span>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Show merge section ONLY for merged -->
    <div class="mb-6 border rounded p-3" v-if="attachMode === 'merged'">
      <div class="flex items-center justify-between">
        <label class="uppercase font-semibold text-sm">Merged attachments</label>
      </div>

      <div class="mt-4 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-semibold text-sm">Merge all selected <strong>Job Sheets</strong> into one file</div>
            <small class="text-gray-500">Creates a single PDF from all completed Job Sheets.</small>
          </div>
          <InputSwitch v-model="mergeJobsEnabled" />
        </div>
        <div v-if="mergeJobsEnabled" class="mt-2">
          <label class="block text-xs uppercase font-semibold mb-1">Merged Job Sheets file name</label>
          <input class="bge-input bge-input-spacing rounded w-full"
                 v-model="mergeJobsFileName"
                 type="text"
                 placeholder="Schedule-XXX-JobSheets.pdf" />
        </div>

        <div class="flex items-center justify-between">
          <div>
            <div class="font-semibold text-sm">Merge all selected <strong>Certificates</strong> into one file</div>
            <small class="text-gray-500">Creates a single PDF from all completed Certificates.</small>
          </div>
          <InputSwitch v-model="mergeCertsEnabled" />
        </div>
        <div v-if="mergeCertsEnabled" class="mt-2">
          <label class="block text-xs uppercase font-semibold mb-1">Merged Certificates file name</label>
          <input class="bge-input bge-input-spacing rounded w-full"
                 v-model="mergeCertsFileName"
                 type="text"
                 placeholder="Schedule-XXX-Certificates.pdf" />
        </div>

        <small class="block text-gray-500 mt-2">
          When selected, the email will include exactly two merged PDFs (Job Sheets, Certificates) instead of individual files.
        </small>
      </div>
    </div>

    <div class="flex justify-end space-x-2">
      <AppButton color="gray" @click="setAsDefault" label="Set as Default"></AppButton>
      <AppButton color="blue" @click="sendJobSheetEmail" label="Send Email">
        <template v-slot:icon>
          <Icon iconType="send"/>
        </template>
      </AppButton>
    </div>

    <div class="mt-10">
      <EmailQueueTable ref="emailQueueTable" :referenceId="schedule.schedule_id"></EmailQueueTable>
    </div>
  </div>
</template>

<script>
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import StyledTextEditor from "@/components/StyledTextEditor.vue";
import notificationMixin from "@/mixins/notificationMixin";
import EmailQueueTable from "@/views/HR/Sales/Components/EmailQueueTable.vue";
import RadioButton from "primevue/radiobutton";

export default {
  name: "ScheduleBulkEmailJobSheets",
  mixins: [notificationMixin],
  components: {
    Icon,
    AppButton,
    EmailQueueTable,
    StyledTextEditor,
    RadioButton
  },
  props: {
    schedule: {type: Object, required: true}, // Pass full schedule object as prop
  },
  data() {
    const customer = this.schedule.customer || {};
    const engineer = this.schedule.engineer || {};
    const recipientEmail = (customer.default_person && customer.default_person.email) ? customer.default_person.email : '';
    const customerName = customer.name || '';
    const engineerName = engineer.name || (engineer.first_name ? `${engineer.first_name} ${engineer.last_name}` : '');
    const scheduledDate = this.schedule.scheduled_date
        ? new Date(this.schedule.scheduled_date * 1000).toLocaleDateString("en-GB")
        : '';

    const attachmentState = (this.schedule.equipment || []).map(eq => {
      const jobSheet = eq.job_sheet && eq.job_sheet.job_sheet_id ? {
        ...eq.job_sheet,
        selected: true,
        job_sheet_no: eq.job_sheet.job_sheet_no,
        url: `job-sheets/${eq.job_sheet.job_sheet_id}/report/job-sheet`,
        fileName: `${eq.job_sheet.job_sheet_no}-Job-Sheet.pdf`
      } : null;

      const getShortName = (cert) => {
        if (cert && cert.name) {
          const parts = cert.name.trim().split(' ');
          const last = parts[parts.length - 1];
          if (last.length >= 3) return last.substring(0, 3).toUpperCase();
          if (last.length > 0) return last.toUpperCase();
          return cert.name.substring(0, 3).toUpperCase();
        }
        return 'CER';
      };

      const certificates = (eq.job_sheet && Array.isArray(eq.job_sheet.certificates))
          ? eq.job_sheet.certificates.map(cert => ({
            ...cert,
            shortName: getShortName(cert),
            selected: cert.status === 'completed',
            url: `certificate/${cert.certificate_id}/report/${(cert.name || '').toLowerCase()}`,
            fileName: `${cert.name}-${cert.certificate_id}.pdf`
          }))
          : [];

      return {
        equipment_id: eq.equipment_id,
        name: eq.name,
        serial_number: eq.serial_number,
        jobSheet,
        certificates
      };
    });

    const schedId = this.schedule.job_id || this.schedule.schedule_id || '';

    return {
      recipientEmail,
      emailSubject: `Your Job Sheets and Certificates for Schedule ${schedId}`,
      emailMessage:
          `Dear ${customerName},\n\nPlease find attached your job sheets and certificates for your scheduled work.\n\nKind regards,\n${engineerName}`,
      ccEmail: null,
      editorOptions: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{'list': 'ordered'}, {'list': 'bullet'}],
        ]
      },
      attachmentState,
      customerName,
      engineerName,
      scheduledDate,
      selectedFiles: [],
      defaultSubject: '',
      defaultEmailText: '',

      // MERGE MODE (kept for logic)
      mergeEnabled: false,
      mergeJobsEnabled: true,
      mergeCertsEnabled: true,
      mergeJobsFileName: `Schedule-${schedId}-JobSheets.pdf`,
      mergeCertsFileName: `Schedule-${schedId}-Certificates.pdf`,

      // NEW: PrimeVue RadioButton mode
      // Merged is the system default (field request: attachment spam); the
      // customer record's preference (Accounts tab) overrides it either way.
      attachMode: customer.email_attachment_preference === 'separate' ? 'separate' : 'merged', // 'separate' | 'merged'
    };
  },
  mounted() {
    // Engineer names first, so the template's {{engineerName}} is filled in
    this.loadEngineerNames().then(() => this.fetchEmailTemplate());

    // The merged auto-select normally runs in the attachMode watcher; when we
    // START in merged mode the watcher never fires, so apply it here.
    if (this.attachMode === 'merged') {
      this.mergeEnabled = true;
      this.applyMergedAutoSelection();
    }
  },
  watch: {
    // Keep existing logic working without changing send method
    attachMode(val, oldVal) {
      this.mergeEnabled = (val === 'merged');

      // If switching to merged and nothing selected yet, auto-select all completed items
      if (val === 'merged') {
        this.applyMergedAutoSelection();
      }
    }
  },
  computed: {
    selectedJobs() {
      const list = [];
      this.attachmentState.forEach(eq => {
        if (eq.jobSheet && this.selectedFiles.includes(`job_${eq.jobSheet.job_sheet_id}`)) {
          list.push({ url: eq.jobSheet.url, file_name: eq.jobSheet.fileName });
        }
      });
      return list;
    },
    selectedCerts() {
      const list = [];
      this.attachmentState.forEach(eq => {
        (eq.certificates || []).forEach(cert => {
          if (this.selectedFiles.includes(`cert_${cert.certificate_id}`)) {
            list.push({ url: cert.url, file_name: cert.fileName });
          }
        });
      });
      return list;
    }
  },
  methods: {
    applyMergedAutoSelection() {
      if (this.selectedFiles && this.selectedFiles.length > 0) return;

      const all = [];
      this.attachmentState.forEach(eq => {
        if (eq.jobSheet && (eq.jobSheet.status === 'Completed' || eq.jobSheet.status === 'CompletedWithFurtherWork')) {
          all.push(`job_${eq.jobSheet.job_sheet_id}`);
        }
        (eq.certificates || []).forEach(cert => {
          if (cert.status === 'completed') {
            all.push(`cert_${cert.certificate_id}`);
          }
        });
      });
      this.selectedFiles = all;
    },
    replacePlaceholders(template, data) {
      return template.replace(/{{\s*(\w+)\s*}}/g, (match, p1) => data[p1] || '');
    },
    // A job's own engineer field is no longer filled in (engineers are booked
    // per day), so take the names from the job's engineer days, skipping
    // cancelled days. Several engineers are listed comma separated.
    async loadEngineerNames() {
      if (this.engineerName || !this.schedule.schedule_id) return;
      try {
        const response = await this.EquipmentScheduleService.listAssignments(this.schedule.schedule_id, '?limit=100');
        const names = [];
        ((response && response.data) || [])
            .filter(a => a.status !== 'Cancelled')
            .forEach(a => {
              const name = a.engineer ? `${a.engineer.first_name || ''} ${a.engineer.last_name || ''}`.trim() : '';
              if (name && names.indexOf(name) === -1) names.push(name);
            });
        this.engineerName = names.join(', ');
      } catch (error) {
        console.error('Error loading engineers for the email:', error);
      }
    },
    async fetchEmailTemplate() {
      try {
        const template = await this.EmailService.getTemplateByKey('schedule_job_sheet_email_temp');
        this.defaultSubject = this.replacePlaceholders(template.subject || '', {
          jobId: this.schedule.job_id || '',
        });
        this.defaultEmailText = this.replacePlaceholders(template.body || '', {
          customer: this.customerName,
          jobSheetNumber: this.schedule.job_id || this.schedule.schedule_id || '',
          jobDate: this.scheduledDate,
          engineerName: this.engineerName
        });

        this.emailSubject = this.defaultSubject;
        this.emailMessage = this.defaultEmailText;
      } catch (error) {
        console.error('Error fetching email template:', error);
      }
    },
    setAsDefault() {
      this.emailSubject = this.defaultSubject;
      this.emailMessage = this.defaultEmailText;
    },
    ensurePdfExt(name) {
      if (!name) return 'merged.pdf';
      const trimmed = ('' + name).trim();
      return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`;
    },
    async sendJobSheetEmail() {
      // Use the computed arrays so it's correct for both modes
      const selectedJobs = this.selectedJobs;
      const selectedCerts = this.selectedCerts;

      // If NOT merged → individual attachments
      if (!this.mergeEnabled) {
        const attachments = [...selectedJobs, ...selectedCerts];

        const emailData = {
          template_key: 'schedule_job_sheet_email_temp',
          reference_id: this.schedule.schedule_id,
          to: [{ name: this.customerName, email: this.recipientEmail }],
          cc: this.ccEmail ? this.ccEmail : [],
          bcc: [],
          category: 'notification',
          status: 'PENDING',
          data: {
            customer: this.customerName,
            jobDate: this.scheduledDate,
            engineerName: this.engineerName,
            scheduleId: this.schedule.schedule_id,
            jobId: this.schedule.job_id,
          },
          use_custom_subject_body: true,
          custom_subject: this.emailSubject,
          custom_body: this.emailMessage,
          has_attachments: attachments.length > 0,
          attachments,
        };

        try {
          await this.EmailService.createQueueItem(emailData);
          this.notifySuccess('Job Sheet Email sent successfully', 'success');
          await this.$refs.emailQueueTable.loadEmailQueues();
        } catch (error) {
          console.error('Error sending job sheet email:', error);
          this.notifyError('Failed to send job sheet email', 'error');
        }
        return;
      }

      // Merge mode ON → exactly two groups: Job Sheets and Certificates (if any selected)
      const mergeGroups = [];
      const scheduleId = this.schedule.schedule_id;

      if (this.mergeJobsEnabled && selectedJobs.length > 0) {
        mergeGroups.push({
          url: `schedules/${scheduleId}/report/compose`,
          file_name: this.ensurePdfExt(this.mergeJobsFileName || `Schedule-${scheduleId}-JobSheets.pdf`),
          attach_group: 'job',
          files: selectedJobs,
        });
      }
      if (this.mergeCertsEnabled && selectedCerts.length > 0) {
        mergeGroups.push({
          url: `schedules/${scheduleId}/report/compose`,
          file_name: this.ensurePdfExt(this.mergeCertsFileName || `Schedule-${scheduleId}-Certificates.pdf`),
          attach_group: 'cert',
          files: selectedCerts,
        });
      }

      const emailData = {
        template_key: 'schedule_job_sheet_email_temp',
        reference_id: this.schedule.schedule_id,
        to: [{ name: this.customerName, email: this.recipientEmail }],
        cc: this.ccEmail ? this.ccEmail : [],
        bcc: [],
        category: 'notification',
        status: 'PENDING',
        data: {
          customer: this.customerName,
          jobDate: this.scheduledDate,
          engineerName: this.engineerName,
          scheduleId: this.schedule.schedule_id,
          jobId: this.schedule.job_id,
        },
        use_custom_subject_body: true,
        custom_subject: this.emailSubject,
        custom_body: this.emailMessage,
        // MERGE payload
        attachments_mode: 'merge',
        has_attachments: mergeGroups.length > 0,
        attachments: [],
        merge_attachments: mergeGroups,
      };

      try {
        await this.EmailService.createQueueItem(emailData);
        this.notifySuccess('Merged Job Sheet/Certificate Email queued', 'success');
        await this.$refs.emailQueueTable.loadEmailQueues();
      } catch (error) {
        console.error('Error sending merged email:', error);
        this.notifyError('Failed to send merged email', 'error');
      }
    }
  }
};
</script>

<style scoped>
/* Add your custom styles here if needed */
</style>
